(() => {
  'use strict';

  const PREFIX = '[FTR Loop Discount Fix]';
  const LOOP_BUNDLE_API = 'https://api.loopsubscriptions.com/storefront/2023-10/bundle';

  const text = (value) => value == null ? '' : String(value).trim();

  function positiveNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(String(value).split('/').pop());
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  function normalizeId(value) {
    const valueText = text(value);
    return valueText || null;
  }

  function unwrap(payload) {
    if (!payload || payload.success === false) return payload?.data ?? payload;
    return payload.data ?? payload;
  }

  function extractTransactionId(payload) {
    const data = unwrap(payload) || {};
    const candidates = [
      data.transactionId,
      data.bundleTransactionId,
      data.loopBundleGuid,
      data.txnId,
      data.id,
      data.transaction?.transactionId,
      data.transaction?.bundleTransactionId,
      payload?.transactionId,
      payload?.bundleTransactionId,
      payload?.loopBundleGuid,
      payload?.txnId,
      payload?.id
    ];

    for (const candidate of candidates) {
      const id = normalizeId(candidate);
      if (id && id.length >= 16) return id;
    }
    return null;
  }

  function officialDetails(state) {
    return state?.bundleDetails?.data || state?.bundleDetails || {};
  }

  function hostedDetails(state) {
    return state?.hostedBundleDetails?.data || state?.hostedBundleDetails || {};
  }

  function getPublicBundleId(state) {
    const official = officialDetails(state);
    const hosted = hostedDetails(state);
    return normalizeId(
      state?.publicBundleId ||
      official?.nanoId || official?.publicId || official?.id ||
      hosted?.nanoId || hosted?.publicId || hosted?.id
    );
  }

  function getSelectedSellingPlanShopifyId(state, items) {
    return positiveNumber(
      state?.selectedSellingPlanShopifyId ||
      state?.getSelectedSellingPlanId?.() ||
      (items || []).find((item) => item?.selling_plan)?.selling_plan
    );
  }

  function getSelectedQuantity(items) {
    return (items || [])
      .filter((item) => item?.properties?._bundleId)
      .reduce((total, item) => total + Math.max(0, Number(item?.quantity || 0)), 0);
  }

  function boxId(box) {
    return normalizeId(box?.nanoId || box?.boxSizeId || box?.publicId || box?.id);
  }

  function getBoxes(details) {
    details = details?.data || details || {};
    return [
      ...(Array.isArray(details?.boxSizes) ? details.boxSizes : []),
      ...(Array.isArray(details?.variants) ? details.variants : [])
    ];
  }

  function matchesQuantity(box, quantity) {
    const exact = Number(box?.quantity ?? box?.size ?? box?.itemCount);
    if (Number.isFinite(exact) && exact > 0) return exact === quantity;

    const min = Number(box?.minimum ?? box?.min ?? box?.minQuantity ?? box?.minSize ?? box?.minValue);
    const max = Number(box?.maximum ?? box?.max ?? box?.maxQuantity ?? box?.maxSize ?? box?.maxValue);

    return (!Number.isFinite(min) || min <= 0 || quantity >= min) &&
      (!Number.isFinite(max) || max <= 0 || quantity <= max);
  }

  function resolveBox(state, productInfo, quantity) {
    const officialBoxes = getBoxes(officialDetails(state));
    const hostedBoxes = getBoxes(hostedDetails(state));
    const all = [...officialBoxes, ...hostedBoxes];

    const domId = normalizeId(
      productInfo?.querySelector?.(
        "[data-loop-box-size-id][aria-selected='true'], [data-loop-box-size-id].selected"
      )?.dataset?.loopBoxSizeId
    );
    const selectedId = domId || normalizeId(state?.selectedBoxSizeId);

    if (selectedId) {
      const exact = all.find((box) => boxId(box) === selectedId);
      if (exact) return exact;
    }

    return all.find((box) => matchesQuantity(box, quantity)) || null;
  }

  function purchaseType(discount) {
    return text(
      discount?.purchaseType ||
      discount?.purchase_type ||
      discount?.appliesToPurchaseType ||
      discount?.purchaseOption
    ).toUpperCase();
  }

  function isSubscriptionDiscount(discount) {
    const type = purchaseType(discount);
    return type.includes('SUBSCRIPTION') || type.includes('SUBSCRIBE');
  }

  function discountId(discount) {
    return normalizeId(
      discount?.nanoId ||
      discount?.publicId ||
      discount?.discountId ||
      discount?.bundleDiscountId ||
      discount?.id
    );
  }

  function discountCode(discount) {
    return normalizeId(
      discount?.code ||
      discount?.discountCode ||
      discount?.couponCode ||
      discount?.promoCode
    );
  }

  function discountValue(discount) {
    const values = [discount?.value, discount?.discountValue, discount?.percentage, discount?.amount];
    for (const value of values) {
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
    return null;
  }

  function planComputedValue(discount, sellingPlanShopifyId) {
    const map = discount?.sellingPlanComputedDiscounts;
    if (!map || !sellingPlanShopifyId) return null;

    const direct = map[sellingPlanShopifyId] ?? map[String(sellingPlanShopifyId)];
    if (direct != null && Number.isFinite(Number(direct))) return Number(direct);

    const key = Object.keys(map).find(
      (entry) => String(entry).split('/').pop() === String(sellingPlanShopifyId)
    );
    if (!key) return null;

    const value = Number(map[key]);
    return Number.isFinite(value) ? value : null;
  }

  function collectDiscounts(root, output = [], seen = new WeakSet(), depth = 0, source = 'unknown') {
    if (!root || depth > 9) return output;

    if (Array.isArray(root)) {
      root.forEach((entry) => collectDiscounts(entry, output, seen, depth + 1, source));
      return output;
    }

    if (typeof root !== 'object' || seen.has(root)) return output;
    seen.add(root);

    const id = discountId(root);
    const looksLikeDiscount = Boolean(id) && (
      root?.purchaseType != null ||
      root?.purchase_type != null ||
      root?.sellingPlanComputedDiscounts != null ||
      root?.oneTimeDiscount != null ||
      root?.discountValue != null ||
      root?.value != null ||
      root?.percentage != null ||
      root?.bundleDiscountId != null ||
      /discount/i.test(text(root?.__typename || root?.name))
    );

    if (looksLikeDiscount) output.push({ discount: root, source });

    Object.values(root).forEach((value) => {
      if (value && typeof value === 'object') {
        collectDiscounts(value, output, seen, depth + 1, source);
      }
    });

    return output;
  }

  function resolveSubscriptionDiscount(state, sellingPlanShopifyId) {
    const candidates = [];

    const selected = state?.selectedBundleDiscount;
    if (selected && discountId(selected)) {
      candidates.push({ discount: selected, source: 'state.selectedBundleDiscount' });
    }

    collectDiscounts(officialDetails(state), candidates, new WeakSet(), 0, 'official bundle');
    collectDiscounts(hostedDetails(state), candidates, new WeakSet(), 0, 'hosted bundle');

    const deduped = [];
    const seen = new Set();
    candidates.forEach((entry) => {
      const id = discountId(entry.discount);
      const key = `${id || 'none'}:${purchaseType(entry.discount)}:${entry.source}`;
      if (!id || seen.has(key)) return;
      seen.add(key);
      deduped.push(entry);
    });

    function score(entry) {
      const discount = entry.discount;
      let value = 0;

      if (!discountId(discount)) return -1000;
      if (isSubscriptionDiscount(discount)) value += 100;
      if (entry.source === 'state.selectedBundleDiscount') value += 80;
      if (entry.source === 'official bundle') value += 40;
      if (entry.source === 'hosted bundle') value += 30;
      if (planComputedValue(discount, sellingPlanShopifyId) != null) value += 60;
      if (discount?.sellingPlanComputedDiscounts) value += 20;
      if ((discountValue(discount) || 0) > 0) value += 10;
      if (discountCode(discount)) value += 25;

      return value;
    }

    deduped.sort((a, b) => score(b) - score(a));

    let chosen = deduped.find((entry) => isSubscriptionDiscount(entry.discount)) || null;
    if (!chosen && deduped.length === 1) chosen = deduped[0];
    if (!chosen && selected && discountId(selected)) {
      chosen = { discount: selected, source: 'state.selectedBundleDiscount fallback' };
    }

    console.groupCollapsed(`${PREFIX} SUBSCRIPTION DISCOUNT RESOLUTION`);
    console.log('Shopify selling plan ID:', sellingPlanShopifyId);
    console.log('Current selected discount:', selected);
    console.log('Candidates:', deduped.map((entry) => ({
      source: entry.source,
      id: discountId(entry.discount),
      code: discountCode(entry.discount),
      purchaseType: purchaseType(entry.discount),
      value: discountValue(entry.discount),
      computed: planComputedValue(entry.discount, sellingPlanShopifyId),
      score: score(entry),
      discount: entry.discount
    })));
    console.log('Chosen discount source:', chosen?.source || null);
    console.log('Chosen discount:', chosen?.discount || null);
    console.log('Chosen discount ID:', discountId(chosen?.discount));
    console.log('Chosen discount code:', discountCode(chosen?.discount));
    console.groupEnd();

    return chosen;
  }

  function buildProductVariants(items) {
    const quantities = new Map();

    (items || [])
      .filter((item) => item?.properties?._bundleId)
      .forEach((item) => {
        const id = positiveNumber(item?.id || item?.variant_id || item?.variantId);
        const quantity = Number(item?.quantity || 0);
        if (!id || !Number.isFinite(quantity) || quantity <= 0) return;
        quantities.set(id, (quantities.get(id) || 0) + quantity);
      });

    return Array.from(quantities, ([shopifyId, quantity]) => ({ shopifyId, quantity }));
  }

  async function createOfficialTransaction(state, items, productInfo) {
    const bundleId = getPublicBundleId(state);
    const sellingPlanShopifyId = getSelectedSellingPlanShopifyId(state, items);
    const productVariants = buildProductVariants(items);
    const quantity = getSelectedQuantity(items);
    const selectedBox = resolveBox(state, productInfo, quantity);
    const selectedBoxId = boxId(selectedBox);
    const resolvedDiscount = resolveSubscriptionDiscount(state, sellingPlanShopifyId);
    const selectedDiscount = resolvedDiscount?.discount || null;
    const selectedDiscountId = discountId(selectedDiscount);

    if (!bundleId) throw new Error('Loop public bundle ID is missing.');
    if (!sellingPlanShopifyId) throw new Error('Shopify selling plan ID is missing.');
    if (!productVariants.length) throw new Error('Loop bundle product variants are missing.');
    if (!selectedBoxId) throw new Error('Loop public box size ID is missing.');

    const payload = {
      boxSizeId: selectedBoxId,
      sellingPlanShopifyId,
      productVariants,
      quantity: 1,
      ...(selectedDiscountId ? { discountId: selectedDiscountId } : {})
    };

    console.groupCollapsed(`${PREFIX} PUBLIC BYOB TRANSACTION`);
    console.log('Bundle ID:', bundleId);
    console.log('Box size ID:', selectedBoxId);
    console.log('Discount source:', resolvedDiscount?.source || 'none');
    console.log('Discount ID:', selectedDiscountId || null);
    console.log('Discount code:', discountCode(selectedDiscount));
    console.log('Discount:', selectedDiscount);
    console.log('Selling plan Shopify ID:', sellingPlanShopifyId);
    console.log('Product variants:', productVariants);
    console.log('Request payload:', payload);
    console.groupEnd();

    const response = await fetch(
      `${LOOP_BUNDLE_API}/${encodeURIComponent(bundleId)}/transaction`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    const rawText = await response.text();
    let responsePayload = null;
    try {
      responsePayload = rawText ? JSON.parse(rawText) : null;
    } catch (_) {
      responsePayload = rawText;
    }

    console.log(PREFIX, 'Public BYOB transaction response:', {
      status: response.status,
      payload: responsePayload
    });

    if (!response.ok || responsePayload?.success === false) {
      const message =
        responsePayload?.message ||
        responsePayload?.code ||
        `Loop transaction failed (${response.status}).`;
      throw new Error(message);
    }

    const transactionId = extractTransactionId(responsePayload);
    if (!transactionId) throw new Error('Loop transaction did not return a transaction ID.');

    return {
      transactionId,
      payload,
      responsePayload,
      selectedDiscount,
      selectedBox
    };
  }

  function replaceTransactionId(items, transactionId) {
    return (items || []).map((item) => {
      if (!item?.properties?._bundleId) return item;
      return {
        ...item,
        properties: {
          ...item.properties,
          _bundleId: transactionId
        }
      };
    });
  }

  function registerDiscount(transactionId, discount) {
    if (!transactionId || !discount) return;

    window.FTRLoopBundleDiscountByTransaction = window.FTRLoopBundleDiscountByTransaction || {};
    window.FTRLoopBundleDiscountByTransaction[transactionId] = discount;

    try {
      sessionStorage.setItem(
        `ftr-loop-bundle-discount-source:${transactionId}`,
        JSON.stringify(discount)
      );
    } catch (_) {}
  }

  function getRememberedLoopDiscountCodes() {
    try {
      const parsed = JSON.parse(sessionStorage.getItem('ftr-loop-applied-discount-codes') || '[]');
      return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
    } catch (_) {
      return [];
    }
  }

  function rememberLoopDiscountCode(code) {
    if (!code) return [];
    const codes = Array.from(new Set([...getRememberedLoopDiscountCodes(), String(code)]));
    try {
      sessionStorage.setItem('ftr-loop-applied-discount-codes', JSON.stringify(codes));
    } catch (_) {}
    return codes;
  }

  async function applyShopifyDiscountCode(nativeFetch, pending) {
    const code = normalizeId(pending?.code);
    if (!code) return null;

    const codes = rememberLoopDiscountCode(code);
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}cart/update.js`;

    console.groupCollapsed(`${PREFIX} APPLY SHOPIFY DISCOUNT CODE`);
    console.log('Transaction ID:', pending?.transactionId || null);
    console.log('Loop discount code:', code);
    console.log('Codes sent to Shopify:', codes);
    console.groupEnd();

    const response = await nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        discount: codes.join(',')
      })
    });

    let cart = null;
    try {
      cart = await response.clone().json();
    } catch (_) {}

    console.log(PREFIX, 'Shopify discount application response:', {
      status: response.status,
      ok: response.ok,
      discountCode: code,
      cart
    });

    if (!response.ok) {
      throw new Error(`Shopify could not apply Loop discount code ${code}.`);
    }

    try {
      sessionStorage.setItem('ftr-loop-last-applied-discount-code', code);
    } catch (_) {}

    return cart;
  }

  function installCartDiscountCodeApplier() {
    if (window.__FTRLoopCartDiscountCodeApplierInstalled || typeof window.fetch !== 'function') return;
    window.__FTRLoopCartDiscountCodeApplierInstalled = true;

    const nativeFetch = window.fetch.bind(window);

    window.fetch = async function(input, init = {}) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      const isCartAdd = method === 'POST' && /\/cart\/add(?:\.js)?(?:\?|$)/.test(url);

      const response = await nativeFetch(input, init);

      if (!isCartAdd || !response.ok) return response;

      const pending = window.__FTRPendingLoopDiscountCode;
      if (!pending?.code) return response;

      try {
        await applyShopifyDiscountCode(nativeFetch, pending);
        if (window.__FTRPendingLoopDiscountCode === pending) {
          window.__FTRPendingLoopDiscountCode = null;
        }
      } catch (error) {
        console.error(PREFIX, 'Failed to apply Loop discount code to Shopify cart.', error);
      }

      return response;
    };

    console.log(PREFIX, 'Shopify cart discount-code applier installed.');
  }

  function install() {
    installCartDiscountCodeApplier();

    const state = window.FTRLoopBundle;
    if (!state?.prepareCartItems) {
      setTimeout(install, 50);
      return;
    }

    if (state.prepareCartItems.__ftrHostedDiscountFallbackV2) return;

    const originalPrepare = state.prepareCartItems.bind(state);

    const wrappedPrepare = async function(items, productInfo) {
      const prepared = await originalPrepare(items, productInfo);

      if (state.purchaseType !== 'subscription') return prepared;
      if (!Array.isArray(prepared) || !prepared.some((item) => item?.properties?._bundleId)) {
        return prepared;
      }

      const official = await createOfficialTransaction(state, prepared, productInfo || document);
      const corrected = replaceTransactionId(prepared, official.transactionId);
      const code = discountCode(official.selectedDiscount);

      if (official.selectedDiscount) {
        state.selectedBundleDiscount = official.selectedDiscount;
        registerDiscount(official.transactionId, official.selectedDiscount);
      }
      state.selectedBundleVariant = official.selectedBox;

      if (code) {
        window.__FTRPendingLoopDiscountCode = {
          code,
          transactionId: official.transactionId,
          createdAt: Date.now()
        };
        try {
          sessionStorage.setItem(
            `ftr-loop-pending-discount-code:${official.transactionId}`,
            code
          );
        } catch (_) {}
        console.log(PREFIX, 'Pending Loop discount code registered for post-add application:', {
          transactionId: official.transactionId,
          code
        });
      } else {
        console.warn(PREFIX, 'Selected Loop discount has no Shopify discount code.', official.selectedDiscount);
      }

      try {
        const oldTransactionId = prepared.find((item) => item?.properties?._bundleId)?.properties?._bundleId;
        const bundleName = corrected.find((item) => item?.properties?._bundleName)?.properties?._bundleName;

        if (bundleName) {
          sessionStorage.setItem(`ftr-loop-bundle-name:${official.transactionId}`, bundleName);
        }
        if (oldTransactionId) {
          const oldUrl = sessionStorage.getItem(`ftr-loop-bundle-url:${oldTransactionId}`);
          if (oldUrl) sessionStorage.setItem(`ftr-loop-bundle-url:${official.transactionId}`, oldUrl);
        }
      } catch (_) {}

      console.log(PREFIX, 'Final subscription bundle children:', {
        transactionId: official.transactionId,
        discountId: official.payload.discountId || null,
        discountCode: code,
        discount: official.selectedDiscount,
        items: corrected
      });

      return corrected;
    };

    wrappedPrepare.__ftrHostedDiscountFallbackV2 = true;
    state.prepareCartItems = wrappedPrepare;

    console.log(PREFIX, 'Public BYOB transaction with automatic Shopify discount-code application enabled.');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    install();
  }
})();